import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { CodeTabs } from '../../components/CodeTabs';
import { PriceTable } from '../../components/PriceTable';
import { RouteMap } from '../../components/RouteMap';
import { Seo } from '../../components/Seo';
import { BASE_URLS } from '../../data/models';
import { formatUsd, LOWEST_INPUT_PRICE, PRICES } from '../../data/prices';
import { localizedPath } from '../../i18n/config';
import { useLocale } from '../../i18n/useLocale';

const FEATURES = [
  ['longRuns', 'var(--line-claude)'],
  ['streaming', 'var(--line-gpt)'],
  ['protocols', 'var(--line-gemini)'],
  ['crypto', 'var(--line-grok)'],
] as const;
const STEPS = ['signIn', 'topUp', 'key'] as const;
const FAQ = ['sdks', 'payment', 'timeouts', 'telegram', 'privacy', 'models'] as const;
const FORMATS = ['openai', 'anthropic', 'gemini'] as const;

const lineColor = (color: string) => ({ '--line': color }) as CSSProperties;

export function LandingPage() {
  const { t } = useTranslation();
  const locale = useLocale();
  const signIn = localizedPath(locale, '/app');
  const models = localizedPath(locale, '/models');

  return (
    <>
      <Seo title={t('meta.landingTitle')} description={t('meta.landingDescription')} />

      <section className="hero">
        <div className="container">
          <h1 className="hero-title">{t('landing.hero.title')}</h1>
          <p className="lead hero-lead">{t('landing.hero.lead')}</p>
          <p className="hero-price">
            {t('landing.hero.price', { price: formatUsd(LOWEST_INPUT_PRICE) })}
          </p>
          <div className="actions">
            <Link className="button button-primary" to={signIn}>
              {t('landing.hero.primary')}
            </Link>
            <Link className="button button-secondary" to={models}>
              {t('landing.hero.secondary')}
            </Link>
          </div>
          <RouteMap />
        </div>
      </section>

      <section className="section" id="prices" aria-labelledby="prices-title">
        <div className="container">
          <h2 id="prices-title">{t('landing.prices.title')}</h2>
          <p className="lead">{t('landing.prices.lead')}</p>
          <PriceTable caption={t('landing.prices.caption')} rows="featured" />
          <p className="note price-note">{t('prices.note', { date: PRICES.updated })}</p>
          <Link className="text-link" to={models}>
            {t('landing.prices.all')}
          </Link>
        </div>
      </section>

      <section className="section" id="code" aria-labelledby="code-title">
        <div className="container split">
          <div>
            <h2 id="code-title">{t('landing.code.title')}</h2>
            <p className="lead">{t('landing.code.lead')}</p>
            <dl className="endpoints">
              {FORMATS.map((format) => (
                <div key={format}>
                  <dt>{t(`landing.code.${format}`)}</dt>
                  <dd>
                    <code>{BASE_URLS[format]}</code>
                  </dd>
                </div>
              ))}
            </dl>
          </div>
          <CodeTabs />
        </div>
      </section>

      <section className="section" aria-labelledby="features-title">
        <div className="container">
          <h2 id="features-title">{t('landing.features.title')}</h2>
          <ul className="features">
            {FEATURES.map(([key, color]) => (
              <li key={key} style={lineColor(color)}>
                <h3>{t(`landing.features.${key}.title`)}</h3>
                <p>{t(`landing.features.${key}.text`)}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="section" id="start" aria-labelledby="steps-title">
        <div className="container">
          <h2 id="steps-title">{t('landing.steps.title')}</h2>
          <ol className="steps">
            {STEPS.map((key) => (
              <li key={key}>
                <h3>{t(`landing.steps.${key}.title`)}</h3>
                <p>{t(`landing.steps.${key}.text`)}</p>
              </li>
            ))}
          </ol>
          <Link className="button button-primary" to={signIn}>
            {t('landing.steps.cta')}
          </Link>
        </div>
      </section>

      <section className="section" id="faq" aria-labelledby="faq-title">
        <div className="container narrow">
          <h2 id="faq-title">{t('landing.faq.title')}</h2>
          <div className="faq">
            {FAQ.map((key) => (
              <details key={key}>
                <summary>{t(`landing.faq.${key}.q`)}</summary>
                <p>{t(`landing.faq.${key}.a`)}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
